import '../../styles.scoped.css'
import {useState} from 'react'
import {Typography, Button, TextField, Select, MenuItem, FormControl, FormHelperText, Chip, IconButton, Alert} from '@mui/material'
import {Add, Delete} from '@mui/icons-material'
import {Controller, useFieldArray} from 'react-hook-form'
import * as yup from 'yup'
import {usStates} from '../../../../utils/helpers'
import {AddressAutocompleteField} from '../../../../components'
import {PAYER_TYPES, SERVICE_CATEGORIES, QUALIFIERS} from '../../../AdminOrganizationDetails/components'
import {emptyBranch, emptyPayer, payerKey, branchNeedsEvv, labelOf, tooLong, showDraftErrors, UP_MENU} from '../../helpers'
import FieldLabel from '../FieldLabel'

export const evvChip = required =>
    required ? (
        <Chip label="EVV Required" variant="outlined" color="success" size="small" sx={{backgroundColor: '#2E7D3214'}} />
    ) : (
        <Chip label="No EVV" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider', backgroundColor: '#00000014'}} />
    )

const humanize = value => value?.replace(/_/g, ' ')

export const serviceSummary = service => [service?.description, humanize(service?.billing_unit), service?.hcpc_code].filter(Boolean).join(' - ')

const payerSchema = yup.object().shape({
    payer_name: yup.string().trim().required('Payor name is required').max(255, tooLong(255)),
    payer_type: yup.string().required('Payor type is required'),
    service_category: yup.string().required('Service category is required'),
    provider_number: yup.string().trim().required('Provider number is required').max(100, tooLong(100)),
    payer_code: yup.string().required('EVV payor code is required').max(20, tooLong(20)),
})

const placeholder = text => <span style={{color: '#aaa'}}>{text}</span>

// A Select bound to the form, showing a placeholder until something is chosen.
export const FormSelect = ({form, name, options, empty, error, helperText}) => (
    <FormControl fullWidth error={error}>
        <Controller
            name={name}
            control={form.control}
            render={({field}) => (
                <Select
                    {...field}
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={selected => (selected ? options.find(option => option.value === selected)?.label || selected : placeholder(empty))}
                    MenuProps={UP_MENU}
                >
                    {options.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            )}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
)

const Payors = ({form, index, payerCodes}) => {
    const base = `branches.${index}`
    const payers = form.watch(`${base}.payers`) || []
    const draftOpen = form.watch(`${base}.payerDraftOpen`)
    const errors = form.formState.errors?.branches?.[index]
    const draftErrors = errors?.payerDraft
    const codeOptions = payerCodes.map(code => ({value: code.code, label: `${code.code} — ${code.payerName}`}))

    const closeDraft = () => {
        form.setValue(`${base}.payerDraft`, emptyPayer())
        form.setValue(`${base}.payerDraftOpen`, false)
        form.clearErrors([`${base}.payerDraft`, `${base}.payerDraftOpen`])
    }

    const save = async () => {
        const draft = form.getValues(`${base}.payerDraft`)
        form.clearErrors(`${base}.payerDraft`)
        try {
            await payerSchema.validate(draft, {abortEarly: false})
        } catch (error) {
            showDraftErrors(form, `${base}.payerDraft`, error)
            return
        }
        const key = payerKey(draft)
        if (payers.some(payer => payerKey(payer) === key)) {
            form.setError(`${base}.payerDraft.payer_name`, {message: 'This payor is already added to this branch'})
            return
        }
        // One payor of the organization, so it cannot differ between branches.
        const elsewhere = form
            .getValues('branches')
            .flatMap(branch => branch.payers || [])
            .find(payer => payerKey(payer) === key)
        if (
            elsewhere &&
            (elsewhere.payer_type !== draft.payer_type ||
                elsewhere.provider_number.trim() !== draft.provider_number.trim() ||
                elsewhere.payer_code !== draft.payer_code)
        ) {
            form.setError(`${base}.payerDraft.payer_name`, {
                message: 'This payor is on another branch with different details. Use the same type, provider number and EVV code.',
            })
            return
        }
        form.setValue(`${base}.payers`, [...payers, {...draft, payer_name: draft.payer_name.trim(), provider_number: draft.provider_number.trim()}])
        closeDraft()
    }

    const remove = position =>
        form.setValue(
            `${base}.payers`,
            payers.filter((_, i) => i !== position),
        )

    return (
        <div className="section">
            <Typography className="section-label" variant="overline" color="text.secondary">
                PAYORS
            </Typography>
            {draftOpen && (
                <>
                    <div className="field">
                        <FieldLabel required>Payor Name</FieldLabel>
                        <TextField
                            placeholder="e.g. Minnesota DHS"
                            fullWidth
                            {...form.register(`${base}.payerDraft.payer_name`)}
                            error={!!draftErrors?.payer_name}
                            helperText={draftErrors?.payer_name?.message}
                        />
                    </div>
                    <div className="form">
                        <div className="form-left field">
                            <FieldLabel required>Payor Type</FieldLabel>
                            <FormSelect
                                form={form}
                                name={`${base}.payerDraft.payer_type`}
                                options={PAYER_TYPES}
                                empty="Select payor type"
                                error={!!draftErrors?.payer_type}
                                helperText={draftErrors?.payer_type?.message}
                            />
                        </div>
                        <div className="form-right field">
                            <FieldLabel required>Service Category</FieldLabel>
                            <FormSelect
                                form={form}
                                name={`${base}.payerDraft.service_category`}
                                options={SERVICE_CATEGORIES}
                                empty="Select category"
                                error={!!draftErrors?.service_category}
                                helperText={draftErrors?.service_category?.message}
                            />
                        </div>
                    </div>
                    <div className="form">
                        <div className="form-left field">
                            <FieldLabel required>Provider Number</FieldLabel>
                            <TextField
                                placeholder="The provider number with this payor"
                                fullWidth
                                {...form.register(`${base}.payerDraft.provider_number`)}
                                error={!!draftErrors?.provider_number}
                                helperText={draftErrors?.provider_number?.message}
                            />
                        </div>
                        <div className="form-right field">
                            <FieldLabel required>EVV Payor Code</FieldLabel>
                            {/* Only a code HHAeXchange knows is accepted, so it is picked from
                                theirs; typed only if that list could not be loaded. */}
                            {codeOptions.length ? (
                                <FormSelect
                                    form={form}
                                    name={`${base}.payerDraft.payer_code`}
                                    options={codeOptions}
                                    empty="e.g. MNCS"
                                    error={!!draftErrors?.payer_code}
                                    helperText={draftErrors?.payer_code?.message}
                                />
                            ) : (
                                <TextField
                                    placeholder="e.g. MNCS"
                                    fullWidth
                                    {...form.register(`${base}.payerDraft.payer_code`)}
                                    error={!!draftErrors?.payer_code}
                                    helperText={draftErrors?.payer_code?.message}
                                />
                            )}
                        </div>
                    </div>
                    <div className="draft-actions">
                        <Button variant="contained" onClick={save}>
                            Save Payor
                        </Button>
                        <Button variant="outlined" onClick={closeDraft}>
                            Cancel
                        </Button>
                    </div>
                    {errors?.payerDraftOpen?.message && (
                        <Typography variant="body2" color="error" style={{marginTop: 8}}>
                            {errors.payerDraftOpen.message}
                        </Typography>
                    )}
                </>
            )}
            {payers.map((payer, position) => (
                <div className="item" key={`${payerKey(payer)}-${position}`}>
                    <div className="item-info">
                        <Typography variant="h9" color="primary">
                            {payer.payer_name}
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {labelOf(PAYER_TYPES, payer.payer_type)} | {labelOf(SERVICE_CATEGORIES, payer.service_category)}
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            Provider #: {payer.provider_number} | EVV payor code: {payer.payer_code}
                        </Typography>
                    </div>
                    <IconButton aria-label={`Remove ${payer.payer_name}`} onClick={() => remove(position)}>
                        <Delete color="error" />
                    </IconButton>
                </div>
            ))}
            {!draftOpen && (
                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Add />}
                    style={{marginTop: 12, borderRadius: 20}}
                    onClick={() => form.setValue(`${base}.payerDraftOpen`, true)}
                >
                    Add another payor
                </Button>
            )}
        </div>
    )
}

const BranchCard = ({form, index, onRemove, serviceCatalog, payerCodes, evvServiceIds}) => {
    const base = `branches.${index}`
    const errors = form.formState.errors?.branches?.[index]
    const branch = form.watch(base)
    const serviceIds = branch?.service_ids || []
    const [selectedServiceId, setSelectedServiceId] = useState('')

    const text = (name, label, placeholderText, {required, registerOptions} = {}) => {
        const error = name.split('.').reduce((node, key) => node?.[key], errors)
        return (
            <>
                <FieldLabel required={required}>{label}</FieldLabel>
                <TextField
                    placeholder={placeholderText}
                    fullWidth
                    {...form.register(`${base}.${name}`, registerOptions)}
                    error={!!error}
                    helperText={error?.message}
                />
            </>
        )
    }

    const selectAddress = selected => {
        form.setValue(`${base}.address.street2`, '')
        form.setValue(`${base}.address.city`, selected?.city)
        form.setValue(`${base}.address.state`, selected?.state)
        form.setValue(`${base}.address.zip`, selected?.postcode)
    }

    const addService = () => {
        if (!selectedServiceId || serviceIds.includes(selectedServiceId)) return
        form.setValue(`${base}.service_ids`, [...serviceIds, selectedServiceId])
        form.clearErrors(`${base}.service_ids`)
        setSelectedServiceId('')
    }

    const removeService = id =>
        form.setValue(
            `${base}.service_ids`,
            serviceIds.filter(serviceId => serviceId !== id),
        )

    return (
        <div className="card">
            <div className="card-header">
                <Typography variant="h7" color="primary">
                    {branch?.main ? 'MAIN BRANCH (HQ)' : `BRANCH ${index + 1}`}
                </Typography>
                {branch?.main ? (
                    <Typography variant="body2" color="error">
                        Can't remove — an organization needs at least one branch
                    </Typography>
                ) : (
                    <Button color="error" startIcon={<Delete />} onClick={onRemove}>
                        Delete branch
                    </Button>
                )}
            </div>
            <div className="field">{text('branch_name', 'Branch Name', 'Enter the branch name', {required: true})}</div>
            {/* Required as a pair: the backend refuses a branch with neither, so
                both are starred and either one satisfies it. */}
            <div className="form">
                <div className="form-left field">{text('email', 'Branch Email', 'branch@example.com', {required: true})}</div>
                <div className="form-right field">
                    {text('phone_number', 'Branch Phone Number', 'Enter the branch phone number', {
                        required: true,
                        // The either-or error sits on the email, so it is checked
                        // again as soon as a phone number is typed.
                        registerOptions: {deps: [`${base}.email`]},
                    })}
                </div>
            </div>
            <Typography variant="caption" color="text.secondary" component="div" style={{marginTop: -8, marginBottom: 16}}>
                Enter at least one: an email or a phone number.
            </Typography>
            <div className="form">
                <div className="form-left field">{text('provider_number', 'Branch NPI', 'Enter the branch NPI', {required: true})}</div>
                <div className="form-right field">{text('umpi', 'Branch UMPI', 'Enter the branch UMPI')}</div>
            </div>

            <div className="section">
                <Typography className="section-label" variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <div className="field">
                    <FieldLabel required>Street Address Line 1</FieldLabel>
                    <AddressAutocompleteField
                        control={form.control}
                        name={`${base}.address.street1`}
                        error={!!errors?.address?.street1}
                        helperText={errors?.address?.street1?.message}
                        onSelect={selectAddress}
                    />
                </div>
                <div className="field">{text('address.street2', 'Street Address Line 2', 'Enter the street address line 2')}</div>
                <div className="form">
                    <div className="form-left field">{text('address.city', 'City', 'Enter the city', {required: true})}</div>
                    <div className="form-right field">
                        <FieldLabel required>State</FieldLabel>
                        <FormSelect
                            form={form}
                            name={`${base}.address.state`}
                            options={usStates.map(state => ({value: state, label: state}))}
                            empty="Select the state"
                            error={!!errors?.address?.state}
                            helperText={errors?.address?.state?.message}
                        />
                    </div>
                </div>
                <div className="field">{text('address.zip', 'Zip Code', 'Enter the zip code', {required: true})}</div>
            </div>

            <div className="section">
                <Typography className="section-label" variant="overline" color="text.secondary">
                    SERVICES
                </Typography>
                <div className="picker-row">
                    <div className="picker">
                        <FieldLabel required>Service</FieldLabel>
                        <FormControl fullWidth error={!!errors?.service_ids}>
                            <Select
                                fullWidth
                                displayEmpty
                                value={selectedServiceId}
                                onChange={e => setSelectedServiceId(e.target.value)}
                                renderValue={selected =>
                                    selected ? serviceCatalog.find(service => service.id === selected)?.name : placeholder('Select the service')
                                }
                                MenuProps={UP_MENU}
                            >
                                {!serviceCatalog.length && (
                                    <MenuItem value="" disabled>
                                        No services available
                                    </MenuItem>
                                )}
                                {serviceCatalog
                                    .filter(service => !serviceIds.includes(service.id))
                                    .map(service => (
                                        // As on the Add Branch page: the billing unit, then the name.
                                        <MenuItem key={service.id} value={service.id}>
                                            <div className="service-option">
                                                {service.billing_unit && (
                                                    <Chip
                                                        label={humanize(service.billing_unit).toUpperCase()}
                                                        size="small"
                                                        style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                                    />
                                                )}
                                                <Typography variant="body2">{service.name}</Typography>
                                            </div>
                                        </MenuItem>
                                    ))}
                            </Select>
                            {errors?.service_ids?.message && <FormHelperText>{errors.service_ids.message}</FormHelperText>}
                        </FormControl>
                    </div>
                    <Button className="picker-button" variant="outlined" startIcon={<Add />} onClick={addService}>
                        Add
                    </Button>
                </div>
                {serviceIds.map(id => {
                    const service = serviceCatalog.find(item => item.id === id)
                    return (
                        <div className="item" key={id}>
                            <div className="item-info">
                                <Typography variant="h9" color="primary">
                                    {service?.name || id}
                                </Typography>
                                <div className="item-meta">
                                    {service?.category && (
                                        <Chip
                                            label={humanize(service.category).toUpperCase()}
                                            size="small"
                                            style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                        />
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        {serviceSummary(service)}
                                    </Typography>
                                </div>
                                <div>{evvChip(service?.requires_evv)}</div>
                            </div>
                            <IconButton aria-label={`Remove ${service?.name || 'service'}`} onClick={() => removeService(id)}>
                                <Delete color="error" />
                            </IconButton>
                        </div>
                    )
                })}
            </div>

            <Payors form={form} index={index} payerCodes={payerCodes} />

            {branchNeedsEvv(branch, evvServiceIds) && (
                <div className="section">
                    <Typography className="section-label" variant="overline" color="text.secondary">
                        EVV ENROLLMENT
                    </Typography>
                    <Alert severity="warning" style={{marginBottom: 16}}>
                        <Typography variant="alertTitle">At least one service on this branch requires EVV.</Typography>
                        <Typography variant="body2">
                            This branch will submit visits under the organization's EVV credentials (set in the next step). It just needs its own
                            office identifier so HHAeXchange can tell it apart from the organization's other branches.
                        </Typography>
                    </Alert>
                    <div className="form">
                        <div className="form-left field">
                            <FieldLabel required>Office Qualifier</FieldLabel>
                            <FormSelect
                                form={form}
                                name={`${base}.evv.office_qualifier`}
                                options={QUALIFIERS}
                                empty="Select office qualifier"
                                error={!!errors?.evv?.office_qualifier}
                                helperText={errors?.evv?.office_qualifier?.message}
                            />
                        </div>
                        <div className="form-right field">
                            {text('evv.office_identifier', 'Office Identifier', 'As HHAeXchange has it on file', {required: true})}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ({form, serviceCatalog, payerCodes, evvServiceIds}) => {
    const {fields, append, remove} = useFieldArray({control: form.control, name: 'branches', keyName: 'fieldId'})

    return (
        <>
            <Typography variant="h6" color="text.primary">
                Branches
            </Typography>
            <Typography className="panel-subtitle" variant="body2" color="text.secondary">
                Add every branch this organization operates — each has its own address, services, and (if needed) EVV setup.
            </Typography>
            <Button
                variant="outlined"
                fullWidth
                startIcon={<Add />}
                style={{marginBottom: 20, borderRadius: 20}}
                onClick={() => append(emptyBranch())}
            >
                Add another branch
            </Button>
            {fields.map((item, index) => (
                <BranchCard
                    key={item.fieldId}
                    form={form}
                    index={index}
                    onRemove={() => remove(index)}
                    serviceCatalog={serviceCatalog}
                    payerCodes={payerCodes}
                    evvServiceIds={evvServiceIds}
                />
            ))}
        </>
    )
}
